<script>
import VUploadItem from './VUploadItem';
import { computed, ref, watch } from 'vue';
import { UploadItem } from '../../models/UploadItem.model';
import { FILE_ACCEPT_DEFAULT } from '../../../common/components/utils/constants';
import { convertBytesToMB } from '../../utils/convertBytesToMB';
import MicroModal from 'micromodal';

/**
 *
 * @version 1.0.0
 * @since
 */
export default {
	name: 'VMedicalDocumentsUploadList',
	components: { VUploadItem },
	props: {
		uploadUrl: {
			required: true,
			type: String,
		},
		uploadedList: {
			required: false,
			type: Array,
			default: () => [],
		},
		label: {
			required: false,
			type: String,
			default: () => '',
		},
		status: {
			required: false,
			type: String,
			default: () => '',
		},
		/**
		 * This prop is made so default values can be set.
		 * But you can still use `accept` the same way you would for `input`
		 * If you set both `accept` & `acceptList`, `accept` has bigger priority.
		 */
		acceptList: {
			required: false,
			type: Array,
			default: () => [...FILE_ACCEPT_DEFAULT],
		},
		editStatus: {
			required: false,
			type: Boolean,
			default: false,
		},
		statuses: {
			required: true,
			type: Object,
		},
		actionsLabels: {
			required: true,
			type: Object,
		},
		modalTriggerId: {
			required: false,
			type: String,
			default: () => '',
		},
		modalLang: {
			required: false,
			type: Object,
		},
		templateLang: {
			required: false,
			type: Object,
		},
		docDescription: {
			required: false,
			type: String,
			default: () => '',
		},
		templateDownloadUrl: {
			required: false,
			type: String,
			default: () => '',
		},
	},
	setup(props, { attrs }) {
		/*************************************************
		 * State
		 **************************************************/
		// TODO: Change with `randomString()` function when everything is merged.
		const id = Math.random().toString(36).substring(2, 9);
		const uploadList = ref(props.uploadedList.map((item) => new UploadItem({ ...item })));
		const accept = computed(() => attrs.accept || props.acceptList.join(', '));
		const acceptLabel = computed(() => accept.value.replaceAll('.', ''));
		const statusLocal = ref(props.status);
		const statuses = ref(props.statuses);
		const colorsStatus = computed(() => ({
			[statuses.value.missing]: 'gray',
			[statuses.value.uploaded]: 'orange',
			[statuses.value.accepted]: 'green',
			[statuses.value.rejected]: 'red',
		}));

		const deleteIndex = ref('');
		const deleteItem = ref({});
		const modifiedFirstBlock = ref('');
		const modalId = computed(() => `modal-${id}`);
		const modalLang = ref(props.modalLang);
		const templateLang = ref(props.templateLang);

		/*************************************************
		 * DOM Event Callbacks
		 **************************************************/
		const onChange = async (event) => {
			for (const file of event.target.files) {
				const { name, size, type } = file;

				const newLength = uploadList.value.push(
					new UploadItem({
						filename: name,
						size: `${convertBytesToMB({ size })} MB`,
						type,
						isLoading: true,
					})
				);
				const fileIndex = newLength - 1;

				const formData = new FormData();
				formData.append('file', file);

				try {
					const {
						data: { data },
					} = await http.post(props.uploadUrl, formData, {
						headers: { 'Content-Type': 'multipart/form-data' },
					});
					uploadList.value[fileIndex] = new UploadItem({ ...data, isLoading: false });
					const wasMissing = statusLocal.value === statuses.value.missing;
					statusLocal.value =
						statusLocal.value === statuses.value.missing
							? statuses.value.uploaded
							: statusLocal.value;
					if (wasMissing) {
						document.dispatchEvent(new CustomEvent('ease:document-uploaded'));
					}
				} catch (err) {
					console.error(err);
					const message = err.response?.data?.message || window.somethingWentWrongMsg;
					uploadList.value[fileIndex].error = message;
					uploadList.value[fileIndex].isLoading = false;
					uploadList.value[fileIndex].enableLocalDelete = true;
				}
			}
		};

		const onDeleteModalItem = ({ index, item }) => {
			deleteIndex.value = index;
			deleteItem.value = item;

			MicroModal.show(modalId.value);
		};

		watch(deleteItem, (newVal) => {
			modifiedFirstBlock.value = modalLang.value.firstBlock.replaceAll('{}', `${newVal.filename}`);
		});

		const onDeleteItem = async () => {
			if (deleteItem.value.enableLocalDelete) {
				uploadList.value.splice(deleteIndex.value, 1);
				MicroModal.close(modalId.value);
				return;
			}
			
			try {
				await http.delete(deleteItem.value.deleteUrl);
				uploadList.value.splice(deleteIndex.value, 1);
				if (!uploadList.value.length) statusLocal.value = statuses.value.missing;
				MicroModal.close(modalId.value);
			} catch (err) {
				console.error(err);
				const message = err.response.data.message || window.somethingWentWrongMsg;
				deleteItem.value.error = message;
			}
		};

		const downloadItem = (index) => {
			const item = props.uploadedList[index.index] || uploadList.value[index.index],
				source = item.downloadUrl,
				fileName = item.filename,
				element = document.createElement('a');

			element.setAttribute('href', source);
			element.setAttribute('download', fileName);
			document.body.appendChild(element);
			element.click();
			element.remove();
		};

		const statusColorsMap = (status) => {
			return colorsStatus.value[status];
		};

		return {
			// State
			id,
			uploadList,
			accept,
			acceptLabel,
			colorsStatus,
			statusLocal,
			modalId,
			modalLang,
			deleteIndex,
			deleteItem,
			modifiedFirstBlock,
			templateLang,

			// DOM Events
			onChange,
			onDeleteItem,
			downloadItem,
			statusColorsMap,
			onDeleteModalItem,
		};
	},
};
</script>

<template>
	<div>
		<div class="upload-document-card" :data-micromodal-trigger="editStatus ? modalTriggerId : null">
			<label class="upload-document-card__label" :for="id">{{ label }}</label>

			<p v-if="docDescription" class="upload-document-card__description mt-12 mb-0">{{ docDescription }}</p>
			<div v-if="templateDownloadUrl" class="upload-document-card__instructions mt-12">
				<div class="info-icon">
					<svg class="icon" role="img">
						<use xlink:href="#icon-info"></use>
					</svg>
				</div>
				<div class="info-instructions">
					<p class="info-instructions__title">{{ templateLang.title }}</p>
					<ul>
						<li>{{ templateLang.inst1 }}</li>
						<li>{{ templateLang.inst2 }}</li>
						<li>{{ templateLang.inst3 }}</li>
					</ul>
					<a class="button button--underline button--underline-secondary button--small" :href="templateDownloadUrl" target="_blank">{{ templateLang.downloadCta }}</a>
				</div>
			</div>

			<label class="upload-document-card__field">
				<span :class="['upload-document-card__status', `${statusColorsMap(statusLocal)}`]">{{
					statusLocal
				}}</span>
				<span v-if="uploadUrl || editStatus" class="upload-document-card__upload">{{
					editStatus ? actionsLabels.edit : actionsLabels.upload
				}}</span>
				<input
					v-if="!editStatus"
					type="file"
					:id="id"
					v-bind="$attrs"
					:accept="accept"
					@change="onChange"
					:disabled="!uploadUrl"
				/>
			</label>
		</div>
		<v-upload-item
			v-for="(item, index) in uploadList"
			:key="item.filename"
			:name="item.filename"
			:delete-url="item.deleteUrl"
			:enable-local-delete="item.enableLocalDelete"
			:info-list="item.infoList"
			:error="item.error"
			:is-loading="item.isLoading"
			@downloadItem="downloadItem({ index })"
			@deleteItem="onDeleteModalItem({ index, item })"
			:modalId="modalId"
		/>

		<div class="modal modal--sm" :id="modalId" aria-hidden="true">
			<div class="modal__overlay" tabindex="-1" data-micromodal-close>
				<div class="modal__container" role="dialog" aria-modal="true" :aria-labelledby="modalId">
					<header class="modal__header">
						<h2 class="modal__title heading-2" :id="`title-${modalId}`">{{ modalLang.title }}</h2>
						<button
							type="button"
							class="modal__close close-modal-btn"
							:aria-label="modalLang.closeCta"
							data-micromodal-close
						>
							<svg class="icon" role="img">
								<use xlink:href="#icon-close"></use>
							</svg>
						</button>
					</header>
					<div class="modal__content">
						<p v-html="modifiedFirstBlock"></p>
						<p>{{ modalLang.secondBlock }}</p>
					</div>
					<div class="modal__actions buttons-wrapper__in-row mt-40">
						<button
							@click="onDeleteItem"
							type="button"
							class="button button__tertiary-red"
							data-confirm="delete"
							data-micromodal-close
						>
							{{ modalLang.confirmCta }}
						</button>
						<a
							href="#"
							class="button button--underline close-modal-btn ml-24"
							data-micromodal-close
							>{{ modalLang.closeCta }}</a
						>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
